import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  SelectChangeEvent,
  MenuItem,
  OutlinedInput,
  MenuProps,
  Tooltip,
  TextField,
  InputAdornment,
  ListSubheader,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Search } from '@mui/icons-material';
import type { ClientCatalogEntry } from '@hooks/useClientCatalog';
import { hasPendingInOtherClients } from './clientSelectorHelpers';

interface ClientSelectorProps {
  clients: ClientCatalogEntry[];
  loading?: boolean;
  value: string;
  onChange: (clientId: string) => void;
  placeholder?: string;
  /** Ids a esconder da lista (ex.: o condomínio já selecionado como destino, ao escolher a origem
   * de um clone). */
  excludeIds?: string[];
  /** Pedido do usuário (10/09): "preciso de uma forma clara para o usuário saber que tem
   * pendências em um condomínio que não está filtrado no momento" — total de pendências (mesmas 6
   * categorias do sino) por condomínio, chave = clientId, mapa esparso (ausente = sem pendência).
   * Opcional: só quem já busca essa informação (`ClientFilter.tsx`) precisa passar — os demais
   * usos deste seletor (Checklist/Patrimônio, diálogo de clone) continuam sem o indicador. */
  pendingByClient?: Record<string, number> | undefined;
}

/**
 * Seletor de condomínio (escolha única), puro/controlado por props — extraído do `<Select>` de
 * `ClientFilter.tsx` (02/09) pra ser reutilizável fora do `DemandsContext`: usado por
 * `ClientFilter` (por baixo, alimentado pelo contexto) e diretamente pelas páginas de
 * Checklist/Patrimônio (estado local da própria página) e pelo diálogo de clone (escolha do
 * condomínio de origem).
 */
const ClientSelector: React.FC<ClientSelectorProps> = ({ clients, loading = false, value, onChange, placeholder = 'Escolha um condomínio', excludeIds, pendingByClient }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const visibleClients = useMemo(() => {
    const excluded = new Set(excludeIds || []);
    return clients.filter((c) => !excluded.has(c.id));
  }, [clients, excludeIds]);

  // Pedido do usuário (11/09): "exiba por ordem alfabética do nome do condomínio" — mesmo critério
  // (localeCompare pt-BR) já usado no seletor equivalente do app mobile (ClientScopeBar.tsx).
  const filteredClients = useMemo(() => {
    const base = searchTerm
      ? visibleClients.filter((client) => client.name.toLowerCase().includes(searchTerm.toLowerCase()))
      : visibleClients;
    return [...base].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' }));
  }, [visibleClients, searchTerm]);

  // Achado real (relato do usuário, 10/09): "ao expandir o componente de filtro... está exibindo
  // o id do condomínio, depois que clica no item... os nomes são exibidos corretamente" — o
  // fallback `|| selectedId` mostrava o ID cru sempre que o catálogo (`useClientCatalog`, buscado
  // no MOUNT) ainda não tinha respondido — acontece toda vez que este componente remonta do zero
  // (ex.: abrir o menu "Filtros", que desmonta o conteúdo ao fechar por padrão do MUI `<Menu>`),
  // já com um condomínio selecionado (persistido). Por volta do tempo em que o usuário clica numa
  // opção, o catálogo já respondeu — daí "corrigir sozinho" ao interagir. Sem fallback pro ID:
  // `undefined` enquanto não resolvido, tratado no `renderValue` abaixo.
  const resolveClientName = (selectedId: string): string | undefined =>
    clients.find((c) => c.id === selectedId || c._id === selectedId)?.name;

  // Pedido do usuário (10/09): indicador sempre visível (sem precisar abrir o dropdown) de que
  // existe pendência em outro condomínio que não o selecionado — ver clientSelectorHelpers.ts.
  const hasPendingElsewhere = useMemo(
    () => hasPendingInOtherClients(pendingByClient, value),
    [pendingByClient, value]
  );

  const menuProps: Partial<MenuProps> = {
    PaperProps: {
      style: {
        maxHeight: 400,
        width: 400,
        minWidth: 350,
      },
    },
    autoFocus: false,
  };

  return (
    <Box sx={{ minWidth: { xs: '100%', md: 200 }, width: { xs: '100%', md: 'auto' } }}>
      <FormControl fullWidth size="small">
        <Select
          value={value || ''}
          onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value)}
          displayEmpty
          input={<OutlinedInput />}
          renderValue={(selected: string) => {
            if (!selected) {
              return <em style={{ color: theme.palette.text.disabled }}>{placeholder}</em>;
            }
            const resolvedName = resolveClientName(selected);
            if (!resolvedName) {
              // Catálogo ainda não respondeu (ou id não encontrado nele) — nunca mostrar o ID cru.
              return <em style={{ color: theme.palette.text.disabled }}>Carregando...</em>;
            }
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>{resolvedName}</span>
                {hasPendingElsewhere && (
                  <Tooltip title="Há pendências em outro condomínio do seu portfólio" arrow>
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'error.main',
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          }}
          MenuProps={menuProps}
          sx={{
            backgroundColor: isMobile ? 'background.paper' : 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isMobile ? 'divider' : '#334155',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          }}
        >
          <ListSubheader>
            <TextField
              size="small"
              autoFocus
              placeholder="Buscar condomínio..."
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>

          {loading ? (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <CircularProgress size={20} />
                <em>Carregando condomínios...</em>
              </Box>
            </MenuItem>
          ) : filteredClients.length === 0 ? (
            <MenuItem disabled>
              <em>Nenhum condomínio encontrado</em>
            </MenuItem>
          ) : (
            filteredClients.map((client) => (
              <MenuItem
                key={client.id}
                value={client.id}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minHeight: '40px',
                  py: 1
                }}
              >
                <Tooltip title={client.name} arrow placement="top">
                  <Box sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    pr: 1,
                    cursor: 'pointer'
                  }}>
                    {client.name}
                  </Box>
                </Tooltip>
                {!!pendingByClient?.[client.id] && (
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      minWidth: 20,
                      height: 20,
                      px: 0.5,
                      borderRadius: '10px',
                      backgroundColor: 'error.main',
                      color: 'error.contrastText',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {pendingByClient[client.id]}
                  </Box>
                )}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ClientSelector;
